import {TFunction} from 'i18next';
import {createContext} from 'react';
import {
  IBanInfo,
  ICommentList,
  ICommentSort,
  ICommentTheme,
  ICommentTrackingProps,
} from './type';
import DescText from '../common/component/DescText';
import {pAlert} from '../common/component/PAlert';
import {pToast} from '../common/container/PToast';
import {ButtonSet} from '../common/type/alert';
import {
  formatFromServerDate,
  getDateDifference,
  getTimeAgoText,
  SHORT_DATE_FORMAT,
} from '../common/util/date';

export const CommentThemeContext = createContext<ICommentTheme>({
  commentBg: 'bg-background',
  replyCommentBg: 'bg-grey-01',
  replyIconColor: 'grey',
  createDtTextStyle: 's12-regular-grey05',
  spoilerTextStyle: 's14-regular-grey12 opacity-2',
  spoilerInfoTextStyle: 's14-regular-red',
  moreIconColor: 'grey',
  buttonTheme: {
    textStyle: 's12-regular-grey08',
    bgStyle: 'bg-grey-01',
    bgStyleForReply: 'bg-grey-02',
    smallDotVariant: 'black',
    effectStyle: {
      color: {
        textStyle: 's12-regular-red',
        bgStyle: 'bg-grey-01',
        bgStyleForReply: 'bg-grey-02',
      },
      comment: {
        textStyle: 's12-regular-white light:text-black',
        bgStyle: 'bg-grey-01',
        bgStyleForReply: 'bg-grey-02',
      },
    },
  },
  deleteBtnTextStyle: 's12-regular-white light:text-black',
  replyMoreTextStyle: 's14-semibold-grey08',
  temporaryTextStyle: 's14-regular-grey08',
  temporaryUserNameTextStyle: 's13-bold-grey13',
  userNameTextStyle: 's13-bold-grey13',
  withdrawUserNameTextStyle: 's13-bold-grey08',
  contentTextStyle: 's14-regular-grey12',
  commentDividerColor: 'bg-grey-02',
  replyDividerColor: 'bg-grey-03',
  inputWrapperStyle:
    'bg-background-02 border-grey-04 light:bg-white light:border-grey-03',
  inputWrapperStyleForReply: 'bg-grey-02 border-grey-04 light:border-grey-03',
  bestBadgeColor: 'red',
});

export const INITIAL_COMMENT_LIST: ICommentList = {
  ids: [],
  sort: 'like',
  sectionMap: {},
  commentMap: {},
};

export const CommentTrackingContext = createContext<ICommentTrackingProps>({
  section: 'comments',
  page: 'content_comments',
});

export function showSortToast(sort: ICommentSort) {
  pToast({
    toastId: sort,
    desc: t =>
      t(
        sort === 'createdDt'
          ? 'comment_order_recent_snackbar'
          : 'comment_order_likes_snackbar'
      ),
  });
}

export function getCommentCreateDtText({
  createdDateTime,
  now,
  t,
}: {
  createdDateTime: string;
  now: number;
  t: TFunction;
}) {
  const createDtDiff = getDateDifference(now, createdDateTime);
  return createDtDiff.totalDays >= 7
    ? formatFromServerDate(createdDateTime, SHORT_DATE_FORMAT)
    : getTimeAgoText({oldDate: createdDateTime, now}, t);
}

export function showBanAlert(banInfo: IBanInfo) {
  banInfo.startDt;
  pAlert({
    title: t => t('comment_regulated_popup_title'),
    desc: t => {
      const banInfoKey = banInfo.isPermanent
        ? PERMANENT_BAN_INFO_KEY
        : TEMPORARY_BAN_INFO_KEY;
      return banInfoKey.map((msg, index) => (
        <DescText
          key={index}
          className={cn('s13-regular-grey08', index <= 1 ? 'mt-10' : 'mt-3')}
          as="span"
        >
          {t(
            msg,
            index === 0 && !banInfo.isPermanent
              ? {start: banInfo.startDt, end: banInfo.endDt}
              : undefined
          )}
        </DescText>
      ));
    },
    isTextAlignCenter: false,
    buttonSet: ButtonSet.Confirm,
  });
}

const PERMANENT_BAN_INFO_KEY = [
  'comment_regulated_popup_date_permanent',
  'comment_regulated_popup_detail_1',
  'comment_regulated_popup_detail_3',
  'comment_regulated_popup_detail_4',
];
const TEMPORARY_BAN_INFO_KEY = [
  'comment_regulated_popup_date_range',
  'comment_regulated_popup_detail_1',
  'comment_regulated_popup_detail_2',
  'comment_regulated_popup_detail_3',
  'comment_regulated_popup_detail_4',
];
